/**
 * Path Extractor
 * Converts route tree into paths object for navigation
 */

import { FALLBACK_PATHS } from './constants';

/**
 * Convert kebab-case to camelCase for path object keys
 * @param {string} str - String to convert
 * @returns {string} camelCase string
 */
const toCamelCase = (str) => {
  return str.replaceAll(/-([a-z])/g, (match, letter) => letter.toUpperCase());
};

/**
 * Extract paths from generated routes
 * Converts route tree into paths object for navigation
 * @param {Array} routes - Route configurations from generateAdminRoutes
 * @returns {Object} Paths object matching the old structure
 */
export const extractPathsFromRoutes = (routes) => {
  const paths = { ...FALLBACK_PATHS };

  /**
   * Recursively extract paths from route structure
   */
  const extractPaths = (routeList, parentPath, parentObj) => {
    routeList.forEach(route => {
      const fullPath = `${parentPath}/${route.path}`;
      const keyHandle = toCamelCase(route.handle);

      // Check if this route has CRUD children (index, :id, add)
      const hasIndexRoute = route.children?.some(child => child.index === true);
      const hasIdRoute = route.children?.some(child => child.path === ':id');
      const hasAddRoute = route.children?.some(child => child.path === 'add');
      
      // Check if children have nested features (not just CRUD routes)
      // This is important for parents like library/taxonomy that have index redirects
      const hasNestedFeatures = route.children?.some(child => 
        child.path && !child.index && child.path !== ':id' && child.path !== 'add'
      );

      // Debug removed - issue resolved!

      // **FIX**: Check for nested features FIRST before checking CRUD patterns
      // This prevents library/taxonomy from being treated as CRUD features
      if (hasNestedFeatures) {
        // Parent group with nested features - create nested structure
        parentObj[keyHandle] = {
          index: fullPath,
        };

        // Filter out index redirects when processing children
        const childrenToProcess = route.children.filter(child => !child.index);
        extractPaths(childrenToProcess, fullPath, parentObj[keyHandle]);
      } else if (hasIndexRoute || hasIdRoute || hasAddRoute) {
        // CRUD feature - create paths structure
        parentObj[keyHandle] = {
          index: fullPath,
        };

        if (hasAddRoute) {
          parentObj[keyHandle].add = `${fullPath}/add`;
        }

        if (hasIdRoute) {
          parentObj[keyHandle].details = `${fullPath}/:id`;
        }
      } else if (route.children && route.children.length > 0) {
        // Parent group without CRUD - create nested structure
        parentObj[keyHandle] = {
          index: fullPath,
        };

        // Process children
        extractPaths(route.children, fullPath, parentObj[keyHandle]);
      } else {
        // Simple route without children
        parentObj[keyHandle] = {
          index: fullPath,
        };
      }
    });
  };

  // Extract paths from routes
  if (routes && routes.length > 0) {
    extractPaths(routes, '/admin', paths.admin);
  }

  return paths;
};

/**
 * Extract paths directly from feature tree (not from routes)
 * This preserves the original structure even when routes use dynamic segments
 * @param {Array} features - Feature tree from API
 * @returns {Object} Paths object
 */
export const extractPathsFromFeatures = (features) => {
  const paths = { ...FALLBACK_PATHS };

  /**
   * Recursively extract paths from feature tree
   */
  const extractPaths = (featureList, parentPath, parentObj) => {
    featureList.forEach(featureWrapper => {
      const feature = featureWrapper.node || featureWrapper;
      const children = featureWrapper.children || [];
      
      const fullPath = `${parentPath}/${feature.handle}`;
      const keyHandle = toCamelCase(feature.handle);

      // Create the path entry
      parentObj[keyHandle] = {
        index: fullPath,
      };

      // If feature has children, recursively process them
      if (children && children.length > 0) {
        extractPaths(children, fullPath, parentObj[keyHandle]);
      }
    });
  };

  // Extract paths from features
  if (features && Array.isArray(features) && features.length > 0) {
    extractPaths(features, '/admin', paths.admin);
  }

  return paths;
};
